import * as React from 'react';

interface IUseAuctionStateParams {
  defaultBet: number;
  nextBets: number[];
  previousBets: number[];
}

interface IUseAuctionStateResult {
  currentBet: number;
  previousBet: number;
  nextBet: number;
  updateBet(bet: number): void;
  decreaseBet(): void;
  increaseBet(): void;
}

export function useAuctionState(params: IUseAuctionStateParams): IUseAuctionStateResult {
  const { defaultBet, nextBets, previousBets } = params;

  const bets = React.useMemo(
    () => [...previousBets, defaultBet, ...nextBets],
    // Не меняем массив ставок, если поменялся defaultBet
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousBets, nextBets],
  );

  const defaultBetIndex = previousBets.length;

  const [bet, setBet] = React.useState(defaultBet);
  const [betIndex, setBetIndex] = React.useState(defaultBetIndex);

  const previousBet = bets[betIndex - 1] ?? null;
  const nextBet = bets[betIndex + 1] ?? null;

  const decreaseBet = React.useCallback(() => {
    setBet(previousBet);
    setBetIndex(betIndex - 1);
  }, [previousBet, betIndex]);

  const increaseBet = React.useCallback(() => {
    setBet(nextBet);
    setBetIndex(betIndex + 1);
  }, [nextBet, betIndex]);

  React.useEffect(
    () => setBetIndex(defaultBetIndex),
    // Если предыдущие/следующие ставки поменялись, значит выполнился запрос check-service и подгрузились обновленные ставки
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousBets, nextBets],
  );

  React.useEffect(
    () => setBet(defaultBet),
    // Если дефолтная ставка поменялась, значит бэк округлил ставку, синхронизируем ее
    [defaultBet],
  );

  return {
    currentBet: bet,
    previousBet,
    nextBet,
    updateBet: setBet,
    decreaseBet,
    increaseBet,
  };
}
